'use client';

import type { CalculatorBaseProps } from '@/components/calculator/CalculatorBase.types';
import CalculatorFormView, { getCalculatorFormViewStaticProps } from '@/components/calculator/CalculatorFormView';
import { useSettings } from '@/providers/SettingsProvider';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { type FC, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

export type { CalculatorBaseProps } from '@/components/calculator/CalculatorBase.types';

const CalculatorBaseInteractiveLazy = dynamic(() => import('@/components/calculator/CalculatorBaseInteractive'));

/** Shown on first paint while the interactive chunk is loading (Suspense catches `React.lazy` suspend). */
const CalculatorStaticShell: FC<{ baseProps: CalculatorBaseProps }> = ({ baseProps }) => {
  const pathname = usePathname();
  const { t } = useTranslation();
  const settings = useSettings();
  return <CalculatorFormView {...getCalculatorFormViewStaticProps(baseProps, pathname, t, settings)} />;
};

/**
 * Renders default field values immediately via Suspense fallback, then Formik/Zustand after the lazy chunk loads.
 * (`next/dynamic` `loading` alone does not reliably run when an ancestor `<Suspense>` has no fallback.)
 */
const CalculatorBase: FC<CalculatorBaseProps> = (props) => {
  if (process.env.NODE_ENV === 'test') {
    // eslint-disable-next-line @typescript-eslint/no-require-imports -- sync require for Jest (avoids dynamic import in tests)
    const CalculatorBaseInteractive = require('./CalculatorBaseInteractive').default as FC<CalculatorBaseProps>;
    return <CalculatorBaseInteractive {...props} />;
  }

  return (
    <Suspense fallback={<CalculatorStaticShell baseProps={props} />}>
      <CalculatorBaseInteractiveLazy {...props} />
    </Suspense>
  );
};

export default CalculatorBase;
